import type { StaticFieldSchema } from 'core/utils/generated/formSchemas'
import { IMMUTABLE_FIELDS } from 'core/utils/immutableFields'
import {
  flattenObject,
  unflattenObject,
  getValueAtPath,
} from 'core/utils/objectUtils'
import { deleteAtPath } from 'core/utils/schemaPaths'

import type { SchemaField } from './schema'

type AnyObject = { [key: string]: any }

// Helper function to get nested value from object
const getNestedValue = (obj: unknown, path: string): unknown => {
  if (!obj || typeof obj !== 'object') return undefined
  return path.split('.').reduce((current: any, key: string) => {
    return current?.[key]
  }, obj)
}

export const prepareInitialValues = (
  data: unknown,
  schema?: Array<StaticFieldSchema> | Array<SchemaField>,
  fieldOverrides?: { [fieldPath: string]: string },
): { [key: string]: any } => {
  // Start by flattening the full original data so non-dirty fields are preserved in form state
  // Ensures fields not present in the schema still round-trip intact
  const flattened = flattenObject((data as { [key: string]: any }) ?? {})

  // Preserve JsonInput fields as-is (not flattened) so they're registered with useForm
  // This ensures validation rules can run for JsonInput fields
  const jsonInputFields: { [key: string]: any } = {}
  if (fieldOverrides) {
    Object.entries(fieldOverrides).forEach(([fieldPath, component]) => {
      if (component === 'JsonInput') {
        const originalValue = getNestedValue(data, fieldPath)
        // Preserve the original value as-is for JsonInput fields (including null/empty arrays)
        // If undefined, use null as default so the field is still registered with useForm
        jsonInputFields[fieldPath] =
          originalValue !== undefined ? originalValue : null
        // Remove flattened keys for this field to avoid conflicts
        Object.keys(flattened).forEach((key) => {
          if (
            key === fieldPath ||
            key.startsWith(`${fieldPath}.`) ||
            key.startsWith(`${fieldPath}[`)
          ) {
            delete flattened[key]
          }
        })
      }
    })
  }

  // Add schema fields that are missing from original data for form tracking
  // This fixes the issue where existing empty arrays get lost, without adding unwanted schema bloat
  if (schema) {
    const addExistingSchemaField = (
      field: StaticFieldSchema | SchemaField,
      parentPath?: string,
    ) => {
      const fieldPath = parentPath ? `${parentPath}.${field.key}` : field.key

      // Skip if this field is a JsonInput override (already handled above)
      if (jsonInputFields[fieldPath] !== undefined) {
        return
      }

      // Check if field exists in original data or needs to be added from schema
      const arrayLengthKey = `${fieldPath}._length`
      const fieldMissingFromFlattened = !(
        fieldPath in flattened || arrayLengthKey in flattened
      )

      // Check if field exists in original nested data (handles null values)
      const originalValue = getNestedValue(data, fieldPath)
      const fieldExistsAsNull = originalValue === null

      // Only add schema fields that don't exist in original data
      // Original data fields are already preserved in the initial flattenObject() call
      if (fieldMissingFromFlattened) {
        if (fieldExistsAsNull || originalValue !== undefined) {
          // Field exists in original data (including null) - preserve with sensible defaults for leaves
          if (fieldExistsAsNull) {
            flattened[fieldPath] = null
          } else if (field.type === 'boolean') {
            flattened[fieldPath] = false
          } else if (field.type === 'number') {
            // Prefer null default for numbers when schema field is present but value is missing
            // This avoids accidentally writing 0 for untouched fields
            flattened[fieldPath] =
              typeof originalValue === 'number' ? originalValue : null
          } else if (field.type === 'array') {
            flattened[arrayLengthKey] = 0
          } else if (field.type === 'object') {
            // Do not add parent object keys; rely on child fields and JsonInput fallback
          } else {
            flattened[fieldPath] = originalValue ?? ''
          }
        } else {
          // Field doesn't exist in original data
          if (field.type === 'object') {
            // Skip adding missing object parent keys
          } else {
            flattened[fieldPath] = null
          }
        }
      }

      // Recursively handle nested fields
      if (field.nested) {
        field.nested.forEach((nestedField) => {
          addExistingSchemaField(nestedField, fieldPath)
        })
      }
    }

    schema.forEach((field) => addExistingSchemaField(field))
  }

  // Merge JsonInput fields back into flattened values
  return { ...flattened, ...jsonInputFields }
}

export const shouldIncludeNewField = (value: any): boolean => {
  if (value === 0) return true
  if (value === '' || value === null || value === undefined || value === false)
    return false
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object')
    return Object.keys(value as { [key: string]: unknown }).length > 0
  return Boolean(value)
}

export const buildFinalNested = (
  original: any,
  current: any,
  originalFlatKeys?: Set<string>,
  currentPath?: string,
): any => {
  if (Array.isArray(current)) {
    const originalWasArray = Array.isArray(original)
    return originalWasArray ? current : current.length > 0 ? current : undefined
  }

  if (
    current === null ||
    current === undefined ||
    typeof current !== 'object'
  ) {
    const originallyPresent = original !== undefined
    // Check if this field existed in the original flattened data
    const wasInOriginalFlat =
      originalFlatKeys && currentPath && originalFlatKeys.has(currentPath)

    if (wasInOriginalFlat) {
      // Field existed in original data - preserve it regardless of value
      return current
    }

    return originallyPresent
      ? current
      : shouldIncludeNewField(current)
      ? current
      : undefined
  }

  const currentObj: AnyObject = current as AnyObject
  const originalObj: AnyObject = (original as AnyObject) ?? {}

  const result: AnyObject = {}
  for (const [key, value] of Object.entries(currentObj)) {
    const childOriginal = Object.prototype.hasOwnProperty.call(originalObj, key)
      ? originalObj[key]
      : undefined
    const childPath = currentPath ? `${currentPath}.${key}` : key
    const child = buildFinalNested(
      childOriginal,
      value,
      originalFlatKeys,
      childPath,
    )
    if (child !== undefined) {
      result[key] = child
    }
  }

  const originallyObject =
    original !== null &&
    typeof original === 'object' &&
    !Array.isArray(original)
  return Object.keys(result).length > 0
    ? result
    : originallyObject
    ? result
    : undefined
}

// Build a path -> type map from schema
const buildTypeMapFromSchema = (
  schema?: Array<StaticFieldSchema> | Array<SchemaField>,
): Map<string, string> => {
  const typeMap = new Map<string, string>()
  if (!schema) return typeMap

  const walk = (fields: Array<any>, parent?: string) => {
    for (const field of fields) {
      const path = parent ? `${parent}.${field.key}` : field.key
      if (field.type) {
        typeMap.set(path, field.type)
      }
      if (field.nested && Array.isArray(field.nested)) {
        walk(field.nested, path)
      }
    }
  }

  walk(schema as Array<any>)
  return typeMap
}

// Coerce flat values to match schema types (numbers remain numbers)
const coerceFlatValuesToSchemaTypes = (
  flat: { [key: string]: any },
  typeMap?: Map<string, string>,
): { [key: string]: any } => {
  if (!typeMap || typeMap.size === 0) return flat
  const result: { [key: string]: any } = {}
  for (const [key, value] of Object.entries(flat)) {
    const expectedType = typeMap.get(key)
    if (expectedType === 'number' && typeof value === 'string') {
      const sanitized = value.replace(/,/g, '').trim()
      if (sanitized.length === 0) {
        result[key] = value
      } else {
        const num = Number(sanitized)
        result[key] = Number.isNaN(num) ? value : num
      }
      continue
    }
    result[key] = value
  }
  return result
}

export const constructFinalData = (
  originalData: { [key: string]: any },
  flatValues: { [key: string]: any },
  schema?: Array<StaticFieldSchema> | Array<SchemaField>,
): { [key: string]: any } => {
  // Filter out undefined values and null values that are used to mark removed array keys
  // But preserve null values that represent actual user data (not array indices)
  const filteredFlatValues = Object.fromEntries(
    Object.entries(flatValues).filter(([key, value]) => {
      if (value === undefined) return false
      // Filter out null values only if they're array index markers (contain brackets or _length)
      // This preserves legitimate null field values while removing array removal markers
      if (value === null && (key.includes('[') || key.endsWith('._length'))) {
        return false
      }

      return true
    }),
  )

  // Coerce to schema types (e.g., ensure numbers are numbers)
  const typeMap = buildTypeMapFromSchema(schema)
  const typeSafeFlatValues = coerceFlatValuesToSchemaTypes(
    filteredFlatValues,
    typeMap,
  )

  // For complex objects (JsonInput), apply permissive filtering to preserve null/false/''/0 inside objects
  const filteredComplexObjects = (obj: any): any => {
    if (typeof obj !== 'object' || obj === null) {
      return obj
    }

    if (Array.isArray(obj)) {
      return obj.map(filteredComplexObjects)
    }

    // For objects, filter out only undefined values, preserve everything else
    const filtered: any = {}
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        filtered[key] = filteredComplexObjects(value)
      }
    }
    return filtered
  }

  // Separate complex objects (JsonInput fields) from flattened keys
  // because blindly unflattening objects can corrupt complete object values
  const complexObjects: { [key: string]: any } = {}
  const flattenedKeys: { [key: string]: any } = {}

  // Identify flattened field paths (primitive and null) to reason about conflicts
  const flattenedPaths = Object.keys(typeSafeFlatValues).filter((key) => {
    const value = typeSafeFlatValues[key]
    return (
      typeof value !== 'object' ||
      value === null ||
      (Array.isArray(value) && value.length === 0)
    )
  })

  Object.entries(typeSafeFlatValues).forEach(([key, value]) => {
    // Use the same logic as flattenedPaths to correctly categorize values
    const isFlattened =
      typeof value !== 'object' ||
      value === null ||
      (Array.isArray(value) && value.length === 0)

    if (!isFlattened) {
      // Do not treat empty arrays as complex objects unless they existed in original data
      // Empty arrays should be filtered by normal shouldIncludeNewField logic
      if (Array.isArray(value) && value.length === 0) {
        const originalValue = getNestedValue(originalData, key)
        if (!Array.isArray(originalValue)) {
          // Empty array that didn't exist in original - treat as flattened key for normal filtering
          flattenedKeys[key] = value
          return
        }
      }

      // Check if this complex object conflicts with any individual field edits
      const hasConflictingFields = flattenedPaths.some((flatPath) =>
        flatPath.startsWith(key + '.'),
      )

      if (!hasConflictingFields) {
        // No conflicts - treat as complex object (JsonInput field)
        complexObjects[key] = filteredComplexObjects(value)
      } else {
        // JsonInput complex objects should take precedence over individual field edits
        // Remove conflicting individual field edits and use the complex object instead
        complexObjects[key] = filteredComplexObjects(value)

        // Only remove flattened keys that actually conflict with this complex object
        // Do not delete all keys with the same prefix — only those that would conflict
        flattenedPaths.forEach((flatPath) => {
          if (flatPath.startsWith(key + '.')) {
            // Only delete if the flattened key would conflict with the complex object structure
            // Complex objects should not override primitive fields that exist independently
            const remainingPath = flatPath.substring(key.length + 1)
            const complexValue = value as { [key: string]: any }
            if (
              complexValue &&
              typeof complexValue === 'object' &&
              remainingPath in complexValue
            ) {
              // Only delete if the complex object actually contains this field
              delete flattenedKeys[flatPath]
            }
          }
        })
      }
    } else {
      // This is a flattened key - let unflattenObject handle it
      flattenedKeys[key] = value
    }
  })

  // Process flattened keys normally
  const nestedCurrent = unflattenObject(flattenedKeys)

  // Prepare complex objects for processing after buildFinalNested
  // Sort by path depth (most specific first) to ensure specific overrides take precedence
  const sortedComplexObjects = Object.entries(complexObjects).sort(
    ([a], [b]) => {
      return b.split('.').length - a.split('.').length
    },
  )

  // Ensure empty arrays are preserved when only _length markers are provided
  const setPath = (obj: AnyObject, path: string, value: any) => {
    const parts = path.split('.')
    let curr: AnyObject = obj
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i]
      if (typeof curr[part] !== 'object' || curr[part] === null) {
        curr[part] = {}
      }
      curr = curr[part] as AnyObject
    }
    curr[parts[parts.length - 1]] = value
  }

  Object.entries(typeSafeFlatValues).forEach(([key, val]) => {
    if (key.endsWith('._length')) {
      const len = typeof val === 'number' ? val : Number(val)
      const arrPath = key.slice(0, -'._length'.length)
      if (Number.isFinite(len) && len === 0) {
        setPath(nestedCurrent, arrPath, [])
      }
    }
  })

  // Pass the original flattened keys to buildFinalNested for proper filtering
  const originalFlatKeys = new Set(Object.keys(flattenObject(originalData)))

  const finalNested = buildFinalNested(
    originalData,
    nestedCurrent,
    originalFlatKeys,
  )

  const result = (finalNested ?? {}) as { [key: string]: any }

  // Apply JsonInput complex objects after buildFinalNested to preserve their values
  // This ensures JsonInput objects bypass the shouldIncludeNewField filtering
  const processedPaths = new Set<string>()

  sortedComplexObjects.forEach(([key, value]) => {
    // Check if this path was already handled by a more specific path
    const isOverriddenByMoreSpecific = Array.from(processedPaths).some(
      (processedPath) => processedPath.startsWith(key + '.'),
    )

    if (!isOverriddenByMoreSpecific) {
      const path = key.split('.')
      let current = result

      // Navigate to the parent object
      for (let i = 0; i < path.length - 1; i++) {
        const part = path[i]
        if (!current[part] || typeof current[part] !== 'object') {
          current[part] = {}
        }
        current = current[part]
      }

      // Set the final value, bypassing buildFinalNested filtering
      current[path[path.length - 1]] = value
      processedPaths.add(key)
    }
  })

  return result
}

export const wrapOnSubmit =
  <T extends { [key: string]: unknown }>(
    originalData: { [key: string]: any },
    mergedSchema?: Array<StaticFieldSchema> | Array<SchemaField>,
    onSubmit?: (data: T | Partial<T>) => Promise<void>,
    options?: { omitPaths?: Array<string> },
  ) =>
  async (flatValues: { [key: string]: any }): Promise<void> => {
    // Apply the same data preservation logic as saveField()
    // Note: fieldOverrides not available here, but this is only used for merging original data
    // JsonInput fields should already be in flatValues from form state
    const originalFlattened = prepareInitialValues(originalData, mergedSchema)
    const mergedFlatValues = { ...originalFlattened, ...flatValues }
    const finalData = constructFinalData(
      originalData,
      mergedFlatValues,
      mergedSchema,
    )

    await onSubmit?.(pruneRequestBody(finalData, options?.omitPaths) as T)
  }

/**
 * The body is rebuilt from the original record, not from what the form showed,
 * so hiding a field is not enough to keep its value off the wire. Every path
 * the form omitted has to be removed here too, at whatever depth it sits.
 */
export const pruneRequestBody = (
  body: AnyObject,
  omitPaths?: Array<string>,
): AnyObject => {
  const pruned = structuredClone(body)

  for (const path of [...IMMUTABLE_FIELDS, ...(omitPaths ?? [])]) {
    deleteAtPath(pruned, path)
  }

  return pruned
}

// ---------------- Array helpers for DynamicForm ----------------

export const reconstructArrayFromFlat = (
  flatValues: { [key: string]: any },
  formKey: string,
): Array<unknown> | undefined => {
  // Check if we have a length marker to determine if this is a managed array
  const lengthKey = `${formKey}._length`
  const length = flatValues[lengthKey]

  // If no length marker, this array is not managed by the form
  if (typeof length !== 'number') {
    return undefined
  }

  // If length is 0, return empty array immediately
  if (length === 0) {
    return []
  }

  // Pull only keys relevant to this array field, but exclude undefined and null values
  const prefix = `${formKey}`
  const subset: { [key: string]: any } = {}
  for (const [k, v] of Object.entries(flatValues)) {
    if (
      (k === prefix ||
        k.startsWith(`${prefix}[`) ||
        k.startsWith(`${prefix}.`)) &&
      v !== undefined &&
      v !== null // Filter out undefined and null values (null marks removed keys)
    ) {
      subset[k] = v
    }
  }

  // If no valid keys found, but we have a length marker, return empty array
  if (Object.keys(subset).length === 0) {
    return []
  }

  const nested = unflattenObject(subset)
  const value = getValueAtPath(nested, formKey)
  return Array.isArray(value) ? (value as Array<unknown>) : undefined
}

export const updateArrayFlatValues = (
  currFlat: { [key: string]: any },
  formKey: string,
  nextArray: Array<unknown>,
): { [key: string]: any } => {
  // Remove existing keys for this array by setting them to null
  // We must use null (not undefined) because useForm merges with defaultValues
  // and undefined values get ignored during object spread
  const result: { [key: string]: any } = {}
  const prefix = `${formKey}`

  for (const [k, v] of Object.entries(currFlat)) {
    if (
      k === prefix ||
      k.startsWith(`${prefix}[`) ||
      k.startsWith(`${prefix}.`) ||
      k === `${prefix}._length`
    ) {
      // Set array keys to null to explicitly override defaultValues
      result[k] = null
      continue
    }
    result[k] = v
  }

  // Write the new array keys using flattenObject with a prefix
  const flatArray = flattenObject(nextArray, formKey)
  // Ensure length marker is present
  flatArray[`${formKey}._length`] = nextArray.length

  return { ...result, ...flatArray }
}

export const pruneFlatArrayKeysByLength = (
  currFlat: { [key: string]: any },
  formKey: string,
  length: number,
): { [key: string]: any } => {
  const result: { [key: string]: any } = {}
  const bracketPrefix = `${formKey}[`
  for (const [k, v] of Object.entries(currFlat)) {
    if (k.startsWith(bracketPrefix)) {
      // extract index inside brackets
      const idxMatch = k.slice(bracketPrefix.length).match(/^(\d+)\]/)
      if (idxMatch) {
        const idx = Number(idxMatch[1])
        if (Number.isFinite(idx) && idx >= length) {
          continue
        }
      }
    }
    result[k] = v
  }
  result[`${formKey}._length`] = length
  return result
}
